import { Grid } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { v4 } from 'uuid';

import {
  SignatureCheckboxIcon,
  SignatureDateIcon,
  SignatureIcon,
  SignatureInitialIcon,
  SignatureTextIcon,
} from 'src/components/icons';
import { useFeatureFlags } from 'src/context/flagsContext';
import {
  CONTRACT_COMPONENT_DEFAULT_HEIGHT,
  CONTRACT_COMPONENT_DEFAULT_WIDTH,
  CONTRACT_COMPONENT_MINIMUM_WIDTH,
  DEFAULT_TEXT_SIGNATURE_LABEL,
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SIGNATURE_COMPONENT_DEFAULT_WIDTH,
  SignatureComponentType,
} from 'src/constants';
import { useContractDateFormat } from 'src/hooks/useContractDateFormat';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { InputType } from 'src/services/api';
import { RootState } from 'src/store';
import {
  AddComponentAction,
  AddPlaceholderComponentAction,
  AddSignatureFormSubmitType,
  DisableComponentClickaway,
  EnableComponentClickAway,
  RemovePlaceholderComponentAction,
  SetSelectedComponent,
} from 'src/store/signaturePage/actions';
import {
  ClientTextComponentFields,
  ComponentCoordinatesType,
} from 'src/store/signaturePage/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { ensureUnreachable } from 'src/utils/common_utils';
import {
  getFieldTypeUsingComponentType,
  hasSignatureCompnentsWithDuplicateLabels,
} from 'src/utils/ContractUtils';

import { useAddDateForm } from '../../UI/Date/useAddDateForm';
import { SignaturePopoverForm } from '../SignatureComponents';
import { SignatureSidebarButton } from '../SignatureComponents/SignatureSidebarButton';

import { useAddCheckboxForm } from './useAddCheckboxForm';
import { useAddMySignatureForm } from './useAddMySignatureForm';
import { useAddTextForm } from './useAddTextForm';
import { useClientInputLabelForm } from './useClientInputLabelForm';

interface SignatureSidebarInputsProps {
  isClientSignatureButton?: boolean;
  signatureClientId?: string;
  signaturePlaceholderComponentElement: HTMLButtonElement | null;
}

interface SignaturePopoverFormValues {
  componentType: SignatureComponentType;
  inputType: InputType;
  isAgree: boolean;
  name: string;
  value: string;
}

export const SignatureSidebarInputs: React.FC<SignatureSidebarInputsProps> = ({
  isClientSignatureButton = false,
  signatureClientId = '',
  signaturePlaceholderComponentElement,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const dateFormat = useContractDateFormat();
  const { EnableContractsCheckbox } = useFeatureFlags();
  const [mySignatureModalOpen, setMySignatureModalOpen] = React.useState(false);
  const [myInitialsModalOpen, setMyInitialsModalOpen] = React.useState(false);
  const [dateModalOpen, setDateModalOpen] = React.useState(false);
  const [textModalOpen, setTextModalOpen] = React.useState(false);
  const [checkboxModalOpen, setCheckboxModalOpen] = React.useState(false);
  const [clientInputLabelModalOpen, setClientInputLabelModalOpen] =
    React.useState(false);
  const [receiverUserId, setReceiverUserId] = React.useState('');
  const [selectedComponentType, setSelectedComponentType] =
    React.useState<SignatureComponentType | null>(null);
  const [componentDropCoordinates, setComponentDropCoordinates] =
    React.useState({
      xPosition: 0,
      yPosition: 0,
    });
  const [placeholderComponent, setPlaceholderComponent] =
    React.useState<HTMLButtonElement | null>();

  const { pageComponents } = useAppSelector(
    (state: RootState) => ({
      activeClients: state.clients.activeClients,
      selectedChannel: state.files.selectedChannel,
      pageComponents: state.signaturePage.pageComponents,
    }),
    shallowEqual,
  );

  React.useEffect(() => {
    const selectedReceiverUser = pageComponents?.filter(
      (component) => component.receiverUserId,
    );
    if (signatureClientId) {
      setReceiverUserId(signatureClientId);
    } else if (selectedReceiverUser.at(0)?.receiverUserId) {
      setReceiverUserId(selectedReceiverUser.at(0)?.receiverUserId || '');
    }
  }, [signatureClientId, pageComponents]);

  // when client label form is open disable the clickaway listner for signature draggable components
  // this will prevent text signature component from de-selecting when clicked anywhere on the client label form modal
  React.useEffect(() => {
    if (clientInputLabelModalOpen) {
      dispatch(DisableComponentClickaway());
    } else {
      dispatch(EnableComponentClickAway());
    }
  }, [clientInputLabelModalOpen]);

  const addClientComponent = (
    componentType: SignatureComponentType,
    key: string,
    coordinates: ComponentCoordinatesType,
  ) => {
    // add a placeholder for  client text component
    dispatch(
      AddPlaceholderComponentAction({
        componentType,
        key,
        fields: { key, value: '' }, // the fields are a temp object
        ...coordinates,
        height: SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
        width: SIGNATURE_COMPONENT_DEFAULT_WIDTH,
      }),
    );
  };

  const addPlaceholderComponent = (
    componentType: SignatureComponentType,
    key: string,
    coordinates: ComponentCoordinatesType,
  ) => {
    dispatch(
      AddPlaceholderComponentAction({
        componentType,
        key,
        fields: { key, value: '' },
        ...coordinates,
        height: 0,
        width: 0,
      }),
    );
  };

  const saveComponent = async (data: AddSignatureFormSubmitType) => {
    const stagedComponent = { ...data };
    stagedComponent.height = SIGNATURE_COMPONENT_DEFAULT_HEIGHT;
    stagedComponent.width = SIGNATURE_COMPONENT_DEFAULT_WIDTH;
    stagedComponent.fieldType = getFieldTypeUsingComponentType(
      stagedComponent.componentType,
    );
    stagedComponent.height = CONTRACT_COMPONENT_DEFAULT_HEIGHT;
    // REQUEST_CHECKBOX is born from the client-textlabel modal which only
    // collects label + isOptional, so the value field is undefined. Seed
    // 'false' so the redux value is always a deterministic string and the
    // API payload includes the field (an undefined value would be dropped
    // from JSON and the BE would not stamp the checkbox).
    if (
      stagedComponent.componentType ===
        SignatureComponentType.REQUEST_CHECKBOX &&
      stagedComponent.value === undefined
    ) {
      stagedComponent.value = 'false';
    }
    const isComponentTypeInitial =
      stagedComponent.componentType === SignatureComponentType.OWNER_INITIAL ||
      stagedComponent.componentType === SignatureComponentType.REQUEST_INITIAL;
    const isUnlabeledCheckbox =
      (stagedComponent.componentType ===
        SignatureComponentType.OWNER_CHECKBOX ||
        stagedComponent.componentType ===
          SignatureComponentType.REQUEST_CHECKBOX) &&
      !stagedComponent.label?.trim();
    if (isUnlabeledCheckbox) {
      // Snug square: the 14×14 box has just enough padding to read as a chip
      // without the full 134px text-field width that other inputs need.
      stagedComponent.width = CONTRACT_COMPONENT_MINIMUM_WIDTH;
    } else if (isComponentTypeInitial) {
      stagedComponent.width = CONTRACT_COMPONENT_MINIMUM_WIDTH;
    } else {
      stagedComponent.width = CONTRACT_COMPONENT_DEFAULT_WIDTH;
    }
    dispatch(AddComponentAction(stagedComponent));

    dispatch(SetSelectedComponent(stagedComponent.fields.key));
  };

  const handleComponentDropped = (
    coordinates: ComponentCoordinatesType & ClientTextComponentFields,
    requestComponentType: SignatureComponentType,
    ownerComponentType: SignatureComponentType,
    openModalFunc: (state: boolean) => void,
  ) => {
    setComponentDropCoordinates({ ...coordinates });
    if (isClientSignatureButton) {
      setSelectedComponentType(requestComponentType);
      // go straight to AddComponent
      const key = v4();

      addClientComponent(requestComponentType, key, {
        ...coordinates,
      });
    } else {
      // Add placeholder component
      const key = v4();
      addPlaceholderComponent(ownerComponentType, key, {
        ...coordinates,
      });
      openModalFunc(true);
    }
  };

  // Opens a modal to collect input label and determine if it's a required field when placing a client component.
  const openClientLabelModal = () => {
    if (isClientSignatureButton) {
      setClientInputLabelModalOpen(true);
    }
  };

  const handleSignatureButtonDrop = (coordinates: ComponentCoordinatesType) => {
    // In contract module, we simply add a component if it's a client signature component as we do not need to collect client label
    if (isClientSignatureButton) {
      const totalSignatureComponents = pageComponents.filter(
        (x) =>
          x.componentType === SignatureComponentType.REQUEST_SIGN &&
          x.inputType === InputType.Client,
      ).length;
      const key = v4();
      const formValues = {
        label: `signature_${totalSignatureComponents + 1}`,
        name: 'Signature',
        isOptional: false,
        componentType: SignatureComponentType.REQUEST_SIGN,
        inputType: InputType.Client,
        height: SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
        width: SIGNATURE_COMPONENT_DEFAULT_WIDTH,
        key,
        fields: {
          key,
          value: '',
        },
        ...coordinates,
      };
      saveComponent(formValues);
      setMySignatureModalOpen(false);
    } else {
      handleComponentDropped(
        coordinates,
        SignatureComponentType.REQUEST_SIGN,
        SignatureComponentType.OWNER_SIGN,
        setMySignatureModalOpen,
      );
      openClientLabelModal();
    }
  };

  const handleInitialsButtonDrop = (coordinates: ComponentCoordinatesType) => {
    // In contract module, we simply add a component if it's a client initial component as we do not need to collect client label
    if (isClientSignatureButton) {
      const totalInitialComponents = pageComponents.filter(
        (x) =>
          x.componentType === SignatureComponentType.REQUEST_INITIAL &&
          x.inputType === InputType.Client,
      ).length;
      const key = v4();
      const formValues = {
        label: `initial_${totalInitialComponents + 1}`,
        name: 'Initials',
        isOptional: false,
        componentType: SignatureComponentType.REQUEST_INITIAL,
        inputType: InputType.Client,
        height: SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
        width: SIGNATURE_COMPONENT_DEFAULT_WIDTH,
        key,
        fields: {
          key,
          value: '',
        },
        ...coordinates,
      };
      saveComponent(formValues);
      setMyInitialsModalOpen(false);
    } else {
      handleComponentDropped(
        coordinates,
        SignatureComponentType.REQUEST_INITIAL,
        SignatureComponentType.OWNER_INITIAL,
        setMyInitialsModalOpen,
      );
      openClientLabelModal();
    }
  };

  const handleDateButtonDrop = (coordinates: ComponentCoordinatesType) => {
    // In contract module, we simply add a component if it's a client date component as we do not need to collect client label
    if (isClientSignatureButton) {
      const totalDateComponents = pageComponents.filter(
        (x) =>
          x.componentType === SignatureComponentType.REQUEST_DATE &&
          x.inputType === InputType.Client,
      ).length;
      const key = v4();
      const formValues = {
        label: `date_${totalDateComponents + 1}`,
        name: 'Date',
        isOptional: false,
        componentType: SignatureComponentType.REQUEST_DATE,
        inputType: InputType.Client,
        height: SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
        width: SIGNATURE_COMPONENT_DEFAULT_WIDTH,
        key,
        fields: {
          key,
          value: '',
        },
        ...coordinates,
      };
      saveComponent(formValues);
      setMySignatureModalOpen(false);
    } else {
      handleComponentDropped(
        coordinates,
        SignatureComponentType.REQUEST_DATE,
        SignatureComponentType.OWNER_DATE,
        setDateModalOpen,
      );
      openClientLabelModal();
    }
  };

  const handleTextButtonDrop = (coordinates: ComponentCoordinatesType) => {
    handleComponentDropped(
      coordinates,
      SignatureComponentType.REQUEST_TEXT,
      SignatureComponentType.OWNER_TEXT,
      setTextModalOpen,
    );

    openClientLabelModal();
  };

  const handleCheckboxButtonDrop = (coordinates: ComponentCoordinatesType) => {
    handleComponentDropped(
      coordinates,
      SignatureComponentType.REQUEST_CHECKBOX,
      SignatureComponentType.OWNER_CHECKBOX,
      setCheckboxModalOpen,
    );

    openClientLabelModal();
  };

  const handleCreateComponent = (formValues: any) => {
    // saveComponent is the source of truth for chip dimensions — including
    // the snug-square sizing for unlabeled checkboxes.
    const componentData = {
      ...formValues,
      ...componentDropCoordinates,
    };
    saveComponent(componentData);
    dispatch(RemovePlaceholderComponentAction());
  };

  const handleSaveComponent = (
    formValues: any,
    callbackFunction: (state: boolean) => void,
  ) => {
    callbackFunction(false);
    handleCreateComponent(formValues);
  };

  const handleCloseClientInputLabelModal = () => {
    // remove last added client text component key
    setClientInputLabelModalOpen(false);
    // remove the component from page
    dispatch(RemovePlaceholderComponentAction());
  };

  // Update client signature button label
  const handleUpdateClientInputLabel = (formValues: any) => {
    const userLabel = formValues.label.trim();
    const trimmedLabel = userLabel || DEFAULT_TEXT_SIGNATURE_LABEL;
    const data = {
      label: trimmedLabel, // if no label is provided by client fallback to default 'Text'
      receiverUserId,
      ...formValues,
    };

    if (isClientSignatureButton) {
      // Duplicate-label check only applies when the IU typed something —
      // checkbox allows an empty label and the persisted value in that case
      // is '', not the DEFAULT_TEXT_SIGNATURE_LABEL fallback. Running the
      // duplicate check against the fallback would falsely block placing a
      // label-free checkbox whenever any other field is named "Text".
      const hasDuplicateLabels =
        userLabel.length > 0 &&
        hasSignatureCompnentsWithDuplicateLabels(pageComponents, trimmedLabel);
      if (hasDuplicateLabels) {
        dispatch(
          alertSnackbar({
            errorMessage: 'This label is already in use. Please try again.',
          }),
        );
        return;
      }
      handleSaveComponent(data, setClientInputLabelModalOpen);
    } else {
      handleSaveComponent(data, setClientInputLabelModalOpen);
    }
  };

  const handleCloseComponent = (callbackFunction: (state: boolean) => void) => {
    callbackFunction(false);
    dispatch(RemovePlaceholderComponentAction());
  };

  React.useEffect(() => {
    setPlaceholderComponent(signaturePlaceholderComponentElement);
  }, [signaturePlaceholderComponentElement]);

  const getEditModalTitleByComponentType = (
    componentType: SignatureComponentType | null,
  ) => {
    if (!componentType) return '';

    switch (componentType) {
      case SignatureComponentType.REQUEST_DATE:
        return t(($) => $.page_contracts.client_field_types.date);
      case SignatureComponentType.REQUEST_SIGN:
        return t(($) => $.page_contracts.client_field_types.signature);
      case SignatureComponentType.REQUEST_TEXT:
        return t(($) => $.page_contracts.client_field_types.textfield);
      case SignatureComponentType.REQUEST_INITIAL:
        return t(($) => $.page_contracts.client_field_types.initial);
      case SignatureComponentType.REQUEST_CHECKBOX:
        return t(($) => $.page_contracts.client_field_types.checkbox);
      case SignatureComponentType.OWNER_DATE:
      case SignatureComponentType.OWNER_SIGN:
      case SignatureComponentType.OWNER_INITIAL:
      case SignatureComponentType.OWNER_TEXT:
      case SignatureComponentType.OWNER_CHECKBOX:
        return '';
      default:
        return ensureUnreachable(componentType);
    }
  };

  const fixedSignatureInputType = {
    inputType: InputType.Fixed,
  };

  const textComponentFormHook = useAddTextForm();
  const checkboxComponentFormHook = useAddCheckboxForm();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Signature"
            onDrop={handleSignatureButtonDrop}
            buttonIcon={SignatureIcon}
            isClientSignatureButton={isClientSignatureButton}
          />
        </Grid>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Initials"
            onDrop={handleInitialsButtonDrop}
            buttonIcon={SignatureInitialIcon}
            isClientSignatureButton={isClientSignatureButton}
          />
        </Grid>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Date"
            onDrop={handleDateButtonDrop}
            buttonIcon={SignatureDateIcon}
            isClientSignatureButton={isClientSignatureButton}
          />
        </Grid>
        <Grid item xs={12}>
          <SignatureSidebarButton
            label="Text"
            onDrop={handleTextButtonDrop}
            buttonIcon={SignatureTextIcon}
            isClientSignatureButton={isClientSignatureButton}
          />
        </Grid>
        {EnableContractsCheckbox && (
          <Grid item xs={12}>
            <SignatureSidebarButton
              label="Checkbox"
              onDrop={handleCheckboxButtonDrop}
              buttonIcon={SignatureCheckboxIcon}
              isClientSignatureButton={isClientSignatureButton}
            />
          </Grid>
        )}
      </Grid>
      <SignaturePopoverForm
        key="client-textlabel"
        initialFormValue={{
          label: '',
          isOptional: false,
          componentType: selectedComponentType,
          inputType: InputType.Client,
        }}
        title={getEditModalTitleByComponentType(selectedComponentType)}
        actionLabel="Add"
        handleSave={handleUpdateClientInputLabel}
        onClose={handleCloseClientInputLabelModal}
        useFormHook={() =>
          useClientInputLabelForm({
            componentType: selectedComponentType,
            // Label is optional for checkbox — the box itself reads as "checkbox"
            // without copy. Required for text since unlabelled text fields have
            // no affordance to tell the CU what to type.
            isRequired:
              selectedComponentType !== SignatureComponentType.REQUEST_CHECKBOX,
          })
        }
        open={clientInputLabelModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />
      <SignaturePopoverForm
        initialFormValue={{
          name: '',
          isAgree: true,
          componentType: SignatureComponentType.OWNER_SIGN,
          ...fixedSignatureInputType,
        }}
        title="Add your signature"
        actionLabel="Add signature"
        handleSave={(formValues: SignaturePopoverFormValues) =>
          handleSaveComponent(formValues, setMySignatureModalOpen)
        }
        onClose={() => handleCloseComponent(setMySignatureModalOpen)}
        useFormHook={() => useAddMySignatureForm('signature')}
        open={mySignatureModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />

      <SignaturePopoverForm
        initialFormValue={{
          name: '',
          componentType: SignatureComponentType.OWNER_INITIAL,
          ...fixedSignatureInputType,
        }}
        title="Add the initials"
        actionLabel="Add initials"
        handleSave={(formValues: any) =>
          handleSaveComponent(formValues, setMyInitialsModalOpen)
        }
        onClose={() => handleCloseComponent(setMyInitialsModalOpen)}
        useFormHook={() => useAddMySignatureForm('initial')}
        open={myInitialsModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />
      <SignaturePopoverForm
        initialFormValue={{
          value: moment().format(dateFormat),
          componentType: SignatureComponentType.OWNER_DATE,
          ...fixedSignatureInputType,
        }}
        title="Add the date"
        actionLabel="Add date"
        handleSave={(formValues: any) =>
          handleSaveComponent(formValues, setDateModalOpen)
        }
        onClose={() => handleCloseComponent(setDateModalOpen)}
        useFormHook={useAddDateForm}
        open={dateModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />
      <SignaturePopoverForm
        initialFormValue={{
          value: '',
          componentType: SignatureComponentType.OWNER_TEXT,
          ...fixedSignatureInputType,
        }}
        title="Add text"
        actionLabel="Add text"
        handleSave={(formValues: any) =>
          handleSaveComponent(formValues, setTextModalOpen)
        }
        onClose={() => handleCloseComponent(setTextModalOpen)}
        useFormHook={() => textComponentFormHook}
        open={textModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />
      <SignaturePopoverForm
        initialFormValue={{
          label: '',
          // Default My checkbox to checked — the most common intent for "I
          // already signed-off" boxes. The IU can untoggle on the canvas
          // via the click-to-toggle handler in SignatureDraggableComponent.
          value: 'true',
          componentType: SignatureComponentType.OWNER_CHECKBOX,
          ...fixedSignatureInputType,
        }}
        title="My checkbox"
        actionLabel="Add"
        handleSave={(formValues: any) =>
          handleSaveComponent(formValues, setCheckboxModalOpen)
        }
        onClose={() => handleCloseComponent(setCheckboxModalOpen)}
        useFormHook={() => checkboxComponentFormHook}
        open={checkboxModalOpen && !!placeholderComponent}
        anchorEl={signaturePlaceholderComponentElement}
      />
    </>
  );
};
